// ============================================================
// HARMONIC CHARTER — Transposer
// ============================================================
// Handles transposition at every level of the data model:
//   - Single chord
//   - Bar
//   - Section
//   - Full song
//
// IMPORTANT: Because chords are stored as scale degrees
// (not absolute note names), transposing a song is primarily
// a key-swap operation. The degree relationships are preserved
// automatically.
//
// The complexity comes from:
//   1. Section-level key overrides (modulations)
//      → Each section's key must be transposed independently,
//        preserving the interval relationship to the song key.
//   2. Slash chord bass notes
//      → These are also stored as degrees, so they transpose
//        with the key automatically. No special handling needed.
//   3. Borrowed chords
//      → Degrees are preserved as-is. The borrowed flag is
//        retained. Reanalysis is deferred to Stage 3.
//   4. Enharmonic spelling
//      → The new key determines spelling (sharps vs flats).
//        This is handled by theory.ts, not here.
//
// ============================================================

import type {
  Song,
  Section,
  Bar,
  Chord,
  Key,
} from './types';
import { PITCH_CLASS, transposeKey, semitoneBetweenKeys } from './theory';

// ------------------------------------------------------------
// CHORD TRANSPOSITION
// ------------------------------------------------------------

/**
 * Transposes a single chord to a new key context.
 *
 * Since chords are stored as degrees, the chord itself does
 * not change — only the key context it belongs to changes.
 * This function is a pass-through for degree-based chords,
 * but is kept explicit for clarity and future extension
 * (e.g. if absolute pitch storage is ever added).
 *
 * For now: returns a shallow copy of the chord unchanged.
 * The degree is relative to whatever key context it lives in.
 */
export function transposeChord(chord: Chord): Chord {
  // Degree-relative storage means the chord is already correct
  // for any key. No mutation needed.
  return { ...chord };
}

// ------------------------------------------------------------
// BAR TRANSPOSITION
// ------------------------------------------------------------

/**
 * Returns a transposed copy of a bar.
 * All chords are shallow-copied (degrees unchanged).
 */
export function transposeBar(bar: Bar): Bar {
  return {
    ...bar,
    chords: bar.chords.map(transposeChord),
  };
}

// ------------------------------------------------------------
// KEY INTERVAL CALCULATION
// ------------------------------------------------------------

/**
 * Calculates the semitone interval between two keys,
 * preserving direction (can be negative for downward transposition).
 *
 * Examples:
 *   C major → D major  = +2
 *   G major → F major  = -2 (or +10 going up — we return shortest path)
 *   C major → Gb major = +6 (tritone — ambiguous, we go up)
 */
export function semitoneInterval(from: Key, to: Key): number {
  const fromPc = PITCH_CLASS[from.root];
  const toPc = PITCH_CLASS[to.root];
  const raw = ((toPc - fromPc) % 12 + 12) % 12;

  // Return shortest path: prefer -6..+6 range
  return raw > 6 ? raw - 12 : raw;
}

// ------------------------------------------------------------
// SECTION TRANSPOSITION
// ------------------------------------------------------------

/**
 * Transposes a section to a new key.
 *
 * If the section has its own key override (modulation), that
 * key is transposed by the same interval as the song key change,
 * preserving the modulation relationship.
 *
 * Example:
 *   Song key: C major → D major (+2 semitones)
 *   Section key override: E major → F# major (+2 semitones)
 *   The section's modulation (up a major third from song key)
 *   is preserved in the new key context.
 *
 * @param section       The section to transpose
 * @param semitones     The interval to transpose by
 */
export function transposeSection(
  section: Section,
  semitones: number,
): Section {
  const newKey = section.key
    ? transposeKey(section.key, semitones)
    : null;

  return {
    ...section,
    key: newKey,
    bars: section.bars.map(transposeBar),
  };
}

// ------------------------------------------------------------
// SONG TRANSPOSITION
// ------------------------------------------------------------

/**
 * Transposes an entire song to a new key.
 *
 * Returns a new Song object — does not mutate the original.
 *
 * All section key overrides are transposed by the same interval,
 * preserving modulation relationships throughout the song.
 *
 * @param song      The song to transpose
 * @param targetKey The new key for the song
 */
export function transposeSong(song: Song, targetKey: Key): Song {
  const semitones = semitoneInterval(song.defaultKey, targetKey);

  return {
    ...song,
    defaultKey: targetKey,
    sections: song.sections.map((section) =>
      transposeSection(section, semitones),
    ),
    updatedAt: new Date().toISOString(),
  };
}

// ------------------------------------------------------------
// CONVENIENCE: TRANSPOSE BY SEMITONES
// ------------------------------------------------------------

/**
 * Transposes a song by a given number of semitones.
 * Positive = up, negative = down.
 *
 * Examples:
 *   transposeSongBySemitones(song, 2)   → up a whole step
 *   transposeSongBySemitones(song, -1)  → down a half step
 *   transposeSongBySemitones(song, 7)   → up a fifth
 */
export function transposeSongBySemitones(song: Song, semitones: number): Song {
  const targetKey = transposeKey(song.defaultKey, semitones);
  return transposeSong(song, targetKey);
}

// ------------------------------------------------------------
// CONVENIENCE: TRANSPOSE SECTION ONLY
// ------------------------------------------------------------

/**
 * Transposes a single section to a new key, independently
 * of the song's default key.
 *
 * Used when the user explicitly sets a section's key override
 * (i.e. adds or changes a modulation).
 *
 * @param section     The section to update
 * @param newKey      The new key for this section
 * @param currentKey  The section's current effective key
 *                    (section.key ?? song.defaultKey)
 */
export function setSectionKey(
  section: Section,
  newKey: Key,
  currentKey: Key,
): Section {
  const semitones = semitoneInterval(currentKey, newKey);
  // transposeSection only carries the section's existing key override
  // through — it won't set a new one if the section had none.
  // We explicitly set the new key here after transposing the bars.
  const transposed = transposeSection(section, semitones);
  return { ...transposed, key: newKey };
}

// ------------------------------------------------------------
// KEY LIST UTILITIES
// ------------------------------------------------------------

/**
 * All 12 major keys in circle-of-fifths order,
 * using conventional enharmonic spellings.
 */
export const MAJOR_KEYS: Key[] = [
  { root: 'C',  scale: 'major' },
  { root: 'G',  scale: 'major' },
  { root: 'D',  scale: 'major' },
  { root: 'A',  scale: 'major' },
  { root: 'E',  scale: 'major' },
  { root: 'B',  scale: 'major' },
  { root: 'F#', scale: 'major' },
  { root: 'Db', scale: 'major' },
  { root: 'Ab', scale: 'major' },
  { root: 'Eb', scale: 'major' },
  { root: 'Bb', scale: 'major' },
  { root: 'F',  scale: 'major' },
];

/**
 * All 12 minor keys in circle-of-fifths order.
 */
export const MINOR_KEYS: Key[] = [
  { root: 'A',  scale: 'minor' },
  { root: 'E',  scale: 'minor' },
  { root: 'B',  scale: 'minor' },
  { root: 'F#', scale: 'minor' },
  { root: 'C#', scale: 'minor' },
  { root: 'G#', scale: 'minor' },
  { root: 'D#', scale: 'minor' },
  { root: 'Bb', scale: 'minor' },
  { root: 'F',  scale: 'minor' },
  { root: 'C',  scale: 'minor' },
  { root: 'G',  scale: 'minor' },
  { root: 'D',  scale: 'minor' },
];

/**
 * Returns all available keys (major + minor) as a flat list.
 * Used to populate key selector UI.
 */
export function getAllKeys(): Key[] {
  return [...MAJOR_KEYS, ...MINOR_KEYS];
}

/**
 * Finds a key object by root string and scale.
 * Returns undefined if not found.
 */
export function findKey(root: string, scale: 'major' | 'minor'): Key | undefined {
  return getAllKeys().find(
    (k) => k.root === root && k.scale === scale,
  );
}